<template>
  <modal
    elem-id="modal_compromisso_aberto"
      size="compactDesktop"
    :title="t('Gestão de Compromisso')"
    :close-label="t('Fechar')"
    save-label=""
    :show="isVisible"
    @close="handleClose"
    @dismiss="handleClose"
  >
    <!-- Content only renders when modal is visible (prevents API calls on mount) -->
    <template v-if="isVisible">
      <!-- Body for compromisso_tipo == 1 -->
    <div v-if="gestaoCompromisso.Model?.compromisso_tipo == 1">

      <!-- Success Message -->
      <Message v-if="successMessage" severity="success" :closable="true" @close="successMessage = ''" style="margin-bottom: 20px;">
        <strong>{{ t('Sucesso') }}!</strong> {{ successMessage }}
      </Message>

      <!-- Action Buttons -->
      <div class="action-buttons-container">
        <!-- Check-in Button -->
        <Button
          v-if="canCheckIn"
          data-testid="compromisso-checkin-btn"
          :label="t('Check-In')"
          icon="pi pi-check"
          severity="success"
          class="action-button"
          @click="handleCheckin"
        />

        <!-- WhatsApp Button -->
        <Button
          v-if="canShowPhone"
          data-testid="compromisso-whatsapp-btn"
          :label="t('WhatsApp')"
          icon="pi pi-whatsapp"
          severity="info"
          class="action-button"
          :as="'a'"
          :href="whatsappUrl"
          target="_blank"
        />

        <!-- Call Button -->
        <Button
          v-if="canShowPhone"
          data-testid="compromisso-ligar-btn"
          :label="t('Ligar')"
          icon="pi pi-phone"
          severity="info"
          class="action-button"
          :as="'a'"
          :href="phoneUrl"
        />

        <!-- GPS Button -->
        <Button
          data-testid="compromisso-gps-btn"
          :label="t('GPS')"
          icon="pi pi-map-marker"
          severity="info"
          class="action-button"
          :as="'a'"
          :href="gpsUrl"
          target="_blank"
        />

        <!-- Remove Button -->
        <Button
          v-if="canRemove"
          data-testid="compromisso-remove-btn"
          :label="t('Remover')"
          icon="pi pi-times"
          severity="danger"
          class="action-button"
          @click="handleRemove"
        />
      </div>

      <!-- Download Receipt Button -->
      <Button
        v-if="canDownloadReceipt"
        data-testid="compromisso-download-recibo-btn"
        :label="t('Download Recibo PDF')"
        icon="pi pi-file-pdf"
        severity="primary"
        class="download-receipt-button"
        @click="handleDownloadReceipt"
      />

      <!-- Compromisso Info Box -->
      <box
        elem-id="boxInformacoesCompromissoGestaoCompromisso"
        :title="t('Compromisso')"
        type="primary">

        <!-- Edit Button -->
        <div v-if="canEdit" class="edit-button-container">
          <Button
            data-testid="compromisso-edit-btn"
            :label="t('Editar')"
            icon="pi pi-pencil"
            severity="secondary"
            outlined
            size="small"
            @click="handleEditCompromisso"
          />
        </div>

        <span
          :data-testid="`compromisso-status-${(gestaoCompromisso.Model.compromisso_status ?? '').toLowerCase().replace(/\s+/g, '-')}`"
          class="sr-only"
        >{{ gestaoCompromisso.Model.compromisso_status }}</span>
        <CompactDataTable :value="compromissoData as any" />

        <h4 class="box-title" style="margin-top: 20px;">{{ t('Serviços') }}</h4>

        <!-- Services Table -->
        <div id="infosServicos" class="table-container">
          <table class="table table-striped tableServicos">
            <tbody>
              <tr v-for="servico in gestaoCompromisso.Model.servicos_compromisso" :key="servico.compromisso_servico_id">
                <td>
                  <i
                    v-if="servico.compromisso_servico_status == 5"
                    class="pi pi-check"
                    :title="t('Serviço realizado')"></i>
                  <i
                    v-if="servico.compromisso_servico_status == 6"
                    class="pi pi-times"
                    :title="t('Serviço cancelado')"></i>
                  <i
                    v-if="servico.compromisso_servico_status == 4"
                    class="pi pi-clock"
                    :title="t('Serviço em andamento')"></i>
                </td>
                <td style="width:80%;">
                  <span :style="{ 'text-decoration': servico.compromisso_servico_status == 6 ? 'line-through' : 'none' }">
                    {{ servico.servico_nome }} - {{ t('Qtd') }}: {{ servico.compromisso_servico_qtd }}
                  </span>
                </td>
                <td class="text-right">
                  <span
                    style="white-space:nowrap"
                    :style="{ 'text-decoration': servico.compromisso_servico_status == 6 ? 'line-through' : 'none' }">
                    {{ n(servico.compromisso_servico_valor ?? 0, 'currency') }}
                  </span>
                </td>
                <td class="animationIf">
                  <Button
                    v-if="canDeleteServico"
                    data-testid="compromisso-servico-excluir-btn"
                    severity="danger"
                    size="small"
                    icon="pi pi-trash"
                    @click="handleDeleteServico(servico)" />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </box>

      <!-- Orcamento Info Box -->
      <box
        elem-id="boxOrcamentoGestaoCompromisso"
        :title="t('Orçamento')"
        type="primary">

        <!-- Edit Button -->
        <div v-if="canEdit" class="edit-button-container">
          <Button
            data-testid="orcamento-edit-btn"
            :label="t('Editar')"
            icon="pi pi-pencil"
            severity="secondary"
            outlined
            size="small"
            @click="handleEditOrcamento"
          />
        </div>

        <CompactDataTable :value="orcamentoData as any" />

        <!-- Occurrences Section -->
        <div id="observacoesOrcamentoGestaoCompromisso" class="occurrences-container" style="margin-top: 20px;">
          <h4>{{ t('Observações do orçamento') }}</h4>
          <occurrences-page
            ref="orcamentoOccurrencesRef"
            owner="orcamento"
            :owner-id="gestaoCompromisso.Orcamento.orcamento_id"
            form-elem-id="inputNovaOcorrenciaOrcamento"
            mode="modal"
            :allow-delete="true"
            :add-label="t('Adicionar observação')"
            :modal-title="t('Nova observação')"
            :description-label="t('Descrição')"
            :close-label="t('Fechar')"
            :save-label="t('Salvar observação')">
          </occurrences-page>
        </div>
      </box>

      <!-- Cliente Info Box -->
      <box
        elem-id="boxClienteGestaoCompromisso"
        :title="t('Cliente')"
        type="primary">

        <!-- Edit Button -->
        <div v-if="canEdit" class="edit-button-container">
          <Button
            data-testid="cliente-edit-btn"
            :label="t('Editar')"
            icon="pi pi-pencil"
            severity="secondary"
            outlined
            size="small"
            @click="handleEditCliente"
          />
        </div>

        <CompactDataTable :value="clienteData as any" value-class="text-capitalize" />

        <!-- Occurrences Section -->
        <div id="observacoesClienteGestaoCompromisso" class="occurrences-container" style="margin-top: 20px;">
          <h4>{{ t('Observações do cliente') }}</h4>
          <occurrences-page
            ref="clienteOccurrencesRef"
            owner="cliente"
            :owner-id="gestaoCompromisso.Orcamento.orcamento_cliente_id"
            form-elem-id="inputNovaOcorrenciaCliente"
            mode="modal"
            :allow-delete="true"
            :add-label="t('Adicionar observação')"
            :modal-title="t('Nova observação')"
            :description-label="t('Descrição')"
            :close-label="t('Fechar')"
            :save-label="t('Salvar observação')">
          </occurrences-page>
        </div>
      </box>

      <!-- Recebimentos Box — quem vê o financeiro (ou a conta em Versão 1) -->
      <box
        v-if="mostraRecebimentos"
        elem-id="boxRecebimentosGestaoCompromisso"
        :title="t('Recebimentos')"
        type="primary">
        <CompactDataTable :value="recebimentosData as any" />

        <div id="recebimentosGestaoCompromisso" class="table-container" style="margin-top: 20px;">
          <Button
            data-testid="compromisso-adicionar-recebimento-btn"
            :label="t('Adicionar recebimento')"
            icon="pi pi-plus"
            severity="primary"
            size="small"
            style="margin-bottom: 15px;"
            id="btnAddRecebimentoGestaoCompromisso"
            @click="handleNovoRecebimento" />

          <!-- Recebimentos table -->
          <recebimento-table-compromisso
            :compromisso-id="Number(gestaoCompromisso.Model.compromisso_id)"
            ref="recebimentoTableRef"
            @edit-recebimento="handleEditRecebimento">
          </recebimento-table-compromisso>
        </div>
      </box>

      <!-- Payment Links Section — feature `baas` -->
      <box
        v-if="baasEnabled"
        elem-id="boxPaymentLinksGestaoCompromisso"
        :title="t('Links de Pagamento')"
        type="primary">
        <PaymentLinksSection
          owner="compromisso"
          :owner-id="gestaoCompromisso.Model.compromisso_id ?? 0"
          :default-amount="Math.round((gestaoCompromisso.Model.compromisso_valor_total ?? 0) * 100)"
          :customer-phone="customerPhone"
          :customer-name="gestaoCompromisso.Orcamento?.orcamento_nome_cliente || ''"
        />
      </box>

      <!-- Total Section -->
      <div id="infosTotal" class="table-container">
        <h3 class="text-right" style="margin-bottom: 12px; font-size: 1.2rem;">{{ t('Total agendado') }}: {{ n(gestaoCompromisso.Model.compromisso_valor_total ?? 0, 'currency') }}</h3>
        <CompactDataTable :value="totalData as any" value-class="text-right" />
      </div>

    </div>
    </template>
  </modal>

  <!-- CheckinWizardModal is centralized in ModalManager.vue - opened via eventBus -->
  <!-- RecebimentoModal is centralized in ModalManager.vue - opened via eventBus -->

  <!-- CompromissoModal, OrcamentosModal, ClienteModal are centralized in ModalManager.vue -->
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { permissionsService } from '../../services/permissionsService';
import { config } from '../../services/configService';
import { log } from '../../utils/DebugLogger';
// Feature `baas` (backend registry, skill feature-flags).
const baasEnabled = config.isFeatureEnabled('baas');

import { useCompromisso } from '../../composables/useCompromisso';
import { useConfirmAction } from '../../composables/useConfirmAction';
import { veOsRecebimentosDoCompromisso } from '../../utils/recebimentosDoCompromisso';
import OccurrencesPage from '../occurrences/OccurrencesPage.vue';
import RecebimentoTableCompromisso from '../recebimento/RecebimentoTableCompromisso.vue';
import CompactDataTable from '../../grid/CompactDataTable.vue';
import PaymentLinksSection from '../payment-links/PaymentLinksSection.vue';
import type { Agenda_BuscarCompromissoRow } from '../../contracts/Agenda_BuscarCompromissoRow';
import type { Agenda_BuscarCompromissoServicoCompromissoRow } from '../../contracts/Agenda_BuscarCompromissoServicoCompromissoRow';
import type { Agenda_BuscarCompromissoOrcamentoDetalhesRow } from '../../contracts/Agenda_BuscarCompromissoOrcamentoDetalhesRow';

type Id = number | string;

/**
 * UI shape of the unwrapped orcamento. The wire contract returns
 * Agenda_BuscarCompromissoOrcamentoRow which has shape
 * { orcamento, servicos } — useCompromisso.fetchCompromisso unwraps the
 * inner orcamento details, so this alias targets the inner row type.
 * Currently no UI-only fields exist on the orcamento side; the alias
 * exists to make the call-site type unambiguous.
 */
type OrcamentoUI = Agenda_BuscarCompromissoOrcamentoDetalhesRow;

interface GestaoState {
  Model: Partial<Agenda_BuscarCompromissoRow>;
  Orcamento: Partial<OrcamentoUI>;
  Cliente: Record<string, unknown>;
  Ocorrencias: unknown[];
  Entity: { Model: Record<string, unknown>; Orcamento: Record<string, unknown> };
}

interface ModalOpenData {
  id?: Id;
  compromissoId?: Id;
  [key: string]: unknown;
}

interface DataRow {
  prop: string;
  value: unknown;
}

// Props
interface Props {
  compromissoId?: Id | null;
}

const props = withDefaults(defineProps<Props>(), {
  compromissoId: null
});

// Emits
const emit = defineEmits<{ 'close': [] }>();

const { t, n } = useI18n();
const { confirmAction } = useConfirmAction();
const {
  fetchCompromisso,
  deleteCompromisso,
  deleteCompromissoServico,
  downloadReceipt
} = useCompromisso();

// Reactive state
const isVisible = ref<boolean>(false);
const gestaoCompromisso = ref<GestaoState>({
  Model: {},
  Orcamento: {},
  Cliente: {},
  Ocorrencias: [],
  Entity: { Model: {}, Orcamento: {} }
});
const successMessage = ref<string>('');
const orcamentoOccurrencesRef = ref<InstanceType<typeof OccurrencesPage> | null>(null);
const clienteOccurrencesRef = ref<InstanceType<typeof OccurrencesPage> | null>(null);
const recebimentoTableRef = ref<InstanceType<typeof RecebimentoTableCompromisso> | null>(null);

const mostraRecebimentos = computed((): boolean => veOsRecebimentosDoCompromisso());
const userGroup = computed((): string => permissionsService.getUserGroup());
const hasPermission = computed((): boolean => {
  const allowedGroups = ['Full', 'Franqueado', 'Gerente', 'Atendente'];
  return allowedGroups.includes(userGroup.value);
});
const canShowPhone = computed((): boolean =>
  gestaoCompromisso.value.Orcamento?.mostrar_telefone_agenda === true || hasPermission.value
);
const canCheckIn = computed((): boolean =>
  gestaoCompromisso.value.Model?.compromisso_status_original === 'Compromisso agendado' ||
  gestaoCompromisso.value.Model?.compromisso_status_original === 'Em andamento'
);
const hasCheckin = computed((): boolean => {
  const checkinDate = gestaoCompromisso.value.Model?.compromisso_checkin;
  return !!(checkinDate && checkinDate !== '2001-01-01 00:00' && checkinDate !== '2001-01-01 00:00:00');
});
const hasCheckout = computed((): boolean =>
  gestaoCompromisso.value.Model?.compromisso_status === 'Realizado'
);
const canEdit = computed((): boolean => hasPermission.value && canCheckIn.value);
const canRemove = computed((): boolean => hasPermission.value && canCheckIn.value);
const canDeleteServico = computed((): boolean => hasPermission.value && canCheckIn.value);
const canDownloadReceipt = computed((): boolean => hasCheckout.value);

const operadorName = computed((): string => {
  const operador = gestaoCompromisso.value.Model?.operador;
  if (!operador) return '';
  return (operador.first_name || '').trim();
});
const whatsappUrl = computed((): string => {
  const { orcamento_ddi: ddi = '', orcamento_ddd: ddd = '', orcamento_telefone: phone = '', orcamento_nome_cliente: name = '' } = gestaoCompromisso.value.Orcamento;
  return `https://api.whatsapp.com/send?phone=${ddi}${ddd}${phone}&text=Olá%20${encodeURIComponent(String(name))}`;
});
const phoneUrl = computed((): string => {
  const { orcamento_ddd: ddd = '', orcamento_telefone: phone = '' } = gestaoCompromisso.value.Orcamento;
  return `tel:${ddd}${phone}`;
});
const gpsUrl = computed((): string => {
  const { orcamento_rua: street = '', orcamento_numero: number = '', orcamento_bairro: neighborhood = '', orcamento_localidade_nome: city = '' } = gestaoCompromisso.value.Orcamento;
  return `https://www.google.com/maps/place/${encodeURIComponent(String(street))},+${number}+-+${encodeURIComponent(String(neighborhood))}+-+${encodeURIComponent(String(city))}`;
});
const customerPhone = computed((): string => {
  const { orcamento_ddi: ddi = '', orcamento_ddd: ddd = '', orcamento_telefone: phone = '' } = gestaoCompromisso.value.Orcamento;
  return `${ddi}${ddd}${phone}`;
});

const compromissoData = computed((): DataRow[] => {
  const model = gestaoCompromisso.value.Model;
  if (!model) return [];
  const data: DataRow[] = [
    { prop: 'ID', value: model.compromisso_id },
    { prop: t('Operador'), value: operadorName.value },
    { prop: t('Status'), value: model.compromisso_status },
    { prop: t('Início'), value: formatDate(model.compromisso_data_hora_inicio) },
    { prop: t('Fim'), value: formatDate(model.compromisso_data_hora_fim) },
    { prop: t('Forma de pagamento'), value: model.compromisso_forma_pagamento_label },
    { prop: t('Parcelas'), value: model.compromisso_numero_parcelas },
    { prop: t('Data de cadastro'), value: formatDate(model.compromisso_data_cadastro) }
  ];
  if (hasCheckin.value) data.push({ prop: 'Check-in', value: formatDate(model.compromisso_checkin) });
  if (hasCheckout.value) data.push({ prop: 'Check-out', value: formatDate(model.compromisso_checkout) });
  return data;
});

const orcamentoData = computed((): DataRow[] => {
  const o = gestaoCompromisso.value.Orcamento;
  if (!o) return [];
  const data: DataRow[] = [
    { prop: 'ID', value: o.orcamento_id },
    { prop: t('Franquia'), value: o.orcamento_franquia_nome },
    { prop: t('Status'), value: o.orcamento_status }
  ];
  const observacao = (o.orcamento_observacao ?? '').trim();
  if (observacao) data.push({ prop: t('Observações'), value: observacao });
  return data;
});

const clienteData = computed((): DataRow[] => {
  const o = gestaoCompromisso.value.Orcamento;
  if (!o) return [];
  const data: DataRow[] = [
    { prop: 'ID', value: o.orcamento_cliente_id },
    { prop: t('Nome'), value: o.orcamento_nome_cliente }
  ];
  if (canShowPhone.value) {
    data.push({ prop: t('Telefone'), value: `${o.orcamento_ddi}${o.orcamento_ddd}${o.orcamento_telefone}` });
  }
  data.push(
    { prop: t('Rua'), value: o.orcamento_rua },
    { prop: t('Bairro'), value: o.orcamento_bairro },
    { prop: t('Número'), value: o.orcamento_numero },
    { prop: t('Localidade'), value: o.orcamento_localidade_nome }
  );
  if (o.orcamento_complemento) {
    data.push({ prop: t('Complemento'), value: o.orcamento_complemento });
  }
  return data;
});

const recebimentosData = computed((): DataRow[] => {
  const model = gestaoCompromisso.value.Model;
  if (!model) return [];
  return [
    { prop: t('Total'), value: n(model.compromisso_valor_total ?? 0, 'currency') },
    { prop: t('Status Financeiro'), value: model.status_financeiro || '-' },
    { prop: t('Valor recebido'), value: n((parseFloat(model.compromisso_valor_recebido ?? '0') || 0) / 100, 'currency') }
  ];
});

const totalData = computed((): DataRow[] => {
  const model = gestaoCompromisso.value.Model;
  if (!model) return [];
  return [
    { prop: t('Meio de pagamento'), value: model.compromisso_forma_pagamento_label },
    { prop: t('Número de parcelas'), value: model.compromisso_numero_parcelas }
  ];
});

// Methods
const formatDate = (dateString: string | undefined): string => {
  if (!dateString || dateString === '2001-01-01 00:00' || dateString === '2001-01-01 00:00:00') return '-';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${day}/${month}/${year} ${hours}:${minutes}`;
};

const loadCompromissoData = async (id: Id): Promise<void> => {
  try {
    eventBus.emit('loading.show');
    log('GestaoCompromissoModal', 'loadCompromissoData', 'Loading compromisso', id);
    const result = await fetchCompromisso(Number(id));
    // useCompromisso.fetchCompromisso normalises the
    // Agenda_BuscarCompromissoResponse envelope into
    // { compromisso, orcamento, cliente: null, ocorrencias } where compromisso
    // and orcamento are the contract Row shapes.
    const compromissoRow = result.compromisso as Partial<Agenda_BuscarCompromissoRow> | undefined;
    const orcamentoRow = result.orcamento as Partial<Agenda_BuscarCompromissoOrcamentoDetalhesRow> | undefined;
    const compromisso: Partial<Agenda_BuscarCompromissoRow> = {
      ...(compromissoRow ?? {}),
      servicos_compromisso: compromissoRow?.servicos_compromisso ?? []
    };
    const orcamento: Partial<OrcamentoUI> = { ...(orcamentoRow ?? {}) };
    const cliente: Record<string, unknown> = (result.cliente ?? {}) as Record<string, unknown>;
    const ocorrencias: unknown[] = result.ocorrencias ?? [];
    gestaoCompromisso.value.Model = compromisso;
    gestaoCompromisso.value.Orcamento = orcamento;
    gestaoCompromisso.value.Cliente = cliente;
    gestaoCompromisso.value.Ocorrencias = ocorrencias;
    gestaoCompromisso.value.Entity.Model = compromisso as Record<string, unknown>;
    gestaoCompromisso.value.Entity.Orcamento = orcamento as Record<string, unknown>;
    eventBus.emit('compromisso.modal.ready', { compromissoId: Number(id) });
    log('GestaoCompromissoModal', 'loadCompromissoData', 'Data loaded successfully');
  } catch (error) {
    log('GestaoCompromissoModal', 'loadCompromissoData', 'Error', error);
    eventBus.emit('globalMessage.error', t('Erro ao carregar compromisso'));
  } finally {
    eventBus.emit('loading.hide');
  }
};

const handleCheckin = (): void => {
  log('GestaoCompromissoModal', 'handleCheckin', 'Opening checkin wizard via eventBus');
  eventBus.emit('modal.checkin-wizard.open', { compromissoId: gestaoCompromisso.value.Model.compromisso_id });
};

const handleEditCompromisso = (): void => {
  log('GestaoCompromissoModal', 'handleEditCompromisso', 'Emitting compromisso.editRequested event');
  eventBus.emit('compromisso.editRequested', gestaoCompromisso.value.Model.compromisso_id);
};

const handleEditOrcamento = (): void => {
  log('GestaoCompromissoModal', 'handleEditOrcamento', 'Emitting orcamento.edit event');
  eventBus.emit('orcamento.edit', gestaoCompromisso.value.Orcamento.orcamento_id);
};

const handleEditCliente = (): void => {
  log('GestaoCompromissoModal', 'handleEditCliente', 'Emitting cliente.edit event');
  const clienteId = gestaoCompromisso.value.Orcamento?.orcamento_cliente_id ||
    (gestaoCompromisso.value.Cliente as Record<string, unknown>)?.cliente_id;
  if (clienteId) eventBus.emit('cliente.edit', clienteId);
};

const handleRemove = (): void => {
  confirmAction({
    message: t('Tem certeza que deseja remover este compromisso da agenda?'),
    header: t('Confirmação'),
    onAccept: async () => {
      try {
        eventBus.emit('loading.show');
        await deleteCompromisso(Number(gestaoCompromisso.value.Model.compromisso_id));
        eventBus.emit('globalMessage.success', t('Compromisso removido com sucesso!'));
        eventBus.emit('compromisso.deleted');
        isVisible.value = false;
      } catch (error) {
        log('GestaoCompromissoModal', 'handleRemove', 'Error', error);
        eventBus.emit('globalMessage.error', t('Erro ao remover compromisso'));
      } finally {
        eventBus.emit('loading.hide');
      }
    }
  });
};

const handleDeleteServico = (servico: Agenda_BuscarCompromissoServicoCompromissoRow): void => {
  confirmAction({
    message: t('Tem certeza que deseja excluir este serviço?'),
    header: t('Confirmação'),
    onAccept: async () => {
      try {
        eventBus.emit('loading.show');
        await deleteCompromissoServico(servico.compromisso_servico_id, servico.orcamento_servico_id);
        eventBus.emit('globalMessage.success', t('Serviço excluído com sucesso!'));
        await loadCompromissoData(gestaoCompromisso.value.Model.compromisso_id as Id);
      } catch (error) {
        log('GestaoCompromissoModal', 'handleDeleteServico', 'Error', error);
        eventBus.emit('globalMessage.error', t('Erro ao excluir serviço'));
      } finally {
        eventBus.emit('loading.hide');
      }
    }
  });
};

const handleDownloadReceipt = async (): Promise<void> => {
  try {
    eventBus.emit('loading.show');
    const compromissoId = gestaoCompromisso.value.Model.compromisso_id;
    const receiptData = {
      compromisso: gestaoCompromisso.value.Model,
      orcamento: gestaoCompromisso.value.Orcamento,
      translations: { t, n }
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const pdfUrl = await downloadReceipt(compromissoId, receiptData as any) as string;
    const completePdfUrl = config.getApiUrl(pdfUrl);
    const link = document.createElement('a');
    link.href = completePdfUrl;
    link.download = completePdfUrl.split('/').pop() ?? 'recibo.pdf';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    log('GestaoCompromissoModal', 'handleDownloadReceipt', 'Download completed');
  } catch (error) {
    log('GestaoCompromissoModal', 'handleDownloadReceipt', 'Error', error);
    eventBus.emit('globalMessage.error', t('Erro ao baixar recibo'));
  } finally {
    eventBus.emit('loading.hide');
  }
};

const handleNovoRecebimento = (): void => {
  log('GestaoCompromissoModal', 'handleNovoRecebimento', 'Opening novo recebimento via eventBus');
  eventBus.emit('modal.recebimento.open', {
    action: 'new',
    franquiaId: gestaoCompromisso.value.Model?.compromisso_franquia_id || 0,
    compromissoId: gestaoCompromisso.value.Model?.compromisso_id || 0
  });
};

const handleEditRecebimento = (recebimentoId: Id): void => {
  log('GestaoCompromissoModal', 'handleEditRecebimento', 'Opening edit recebimento via eventBus', recebimentoId);
};

const handleClose = (): void => {
  log('GestaoCompromissoModal', 'handleClose', 'Closing modal');
  isVisible.value = false;
  gestaoCompromisso.value = {
    Model: {},
    Orcamento: {},
    Cliente: {},
    Ocorrencias: [],
    Entity: { Model: {}, Orcamento: {} }
  };
  successMessage.value = '';
};

const handleGestaoOpen = (payload: ModalOpenData): void => {
  log('GestaoCompromissoModal', 'eventBus:open', 'Received event', payload);
  void loadCompromissoData(payload.compromissoId as Id);
  isVisible.value = true;
};

const handleModalOpen = (data: ModalOpenData): void => {
  log('GestaoCompromissoModal', 'handleModalOpen', 'Standard modal open event received', data);
  if (data?.id || data?.compromissoId) {
    void loadCompromissoData((data.id || data.compromissoId) as Id);
    isVisible.value = true;
  }
};

const handleCompromissoSaved = (): void => {
  if (isVisible.value && gestaoCompromisso.value.Model?.compromisso_id) {
    void loadCompromissoData(gestaoCompromisso.value.Model.compromisso_id);
  }
};

const handleOrcamentoSaved = (): void => {
  if (isVisible.value && gestaoCompromisso.value.Model?.compromisso_id) {
    void loadCompromissoData(gestaoCompromisso.value.Model.compromisso_id);
  }
};

// Lifecycle
onMounted(() => {
  log('GestaoCompromissoModal', 'onMounted', 'Component mounted');
  if (props.compromissoId) {
    void loadCompromissoData(Number(props.compromissoId));
    isVisible.value = true;
  }
  eventBus.on('modal.gestao-compromisso.open', handleModalOpen as (data?: unknown) => void);
  eventBus.on('modal.gestao-compromisso.close', handleClose);
  eventBus.on('gestao-compromisso.open', handleGestaoOpen as (data?: unknown) => void);
  eventBus.on('compromisso.saved', handleCompromissoSaved);
  eventBus.on('orcamento.saved', handleOrcamentoSaved);
});

onBeforeUnmount(() => {
  log('GestaoCompromissoModal', 'onBeforeUnmount', 'Cleaning up event listeners');
  eventBus.off('modal.gestao-compromisso.open', handleModalOpen as (data?: unknown) => void);
  eventBus.off('modal.gestao-compromisso.close', handleClose);
  eventBus.off('gestao-compromisso.open', handleGestaoOpen as (data?: unknown) => void);
  eventBus.off('compromisso.saved', handleCompromissoSaved);
  eventBus.off('orcamento.saved', handleOrcamentoSaved);
});

watch(() => props.compromissoId, (newId) => {
  if (newId) {
    void loadCompromissoData(Number(newId));
    isVisible.value = true;
  }
});

defineExpose({
  open: (compromissoId: Id) => {
    void loadCompromissoData(compromissoId);
    isVisible.value = true;
  },
  close: handleClose
});
</script>

<style scoped>
/* Definition lists - Modern Flexbox layout */
.dl-horizontal {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.dl-horizontal dt {
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
  margin-top: 12px;
  font-size: 0.95rem;
}

/* Tables - Modern styling */
.table {
  width: 100%;
  border-collapse: collapse;
}

.table tbody tr {
  border-bottom: 1px solid #f0f0f0;
  transition: background-color 0.2s ease;
}

.table tbody tr:hover {
  background-color: #f9f9f9;
}

.table tbody tr:last-child {
  border-bottom: none;
}

.table tbody td {
  padding: 6px 8px;
  vertical-align: middle;
  font-size: 0.85rem;
  line-height: 1.3;
}

.table tbody td:first-child {
  width: 30px;
  text-align: center;
}

.table tbody td:first-child i {
  font-size: 14px;
}

.table tbody td:first-child i.pi-check {
  color: #00a65a;
}

.table tbody td:first-child i.pi-times {
  color: #dd4b39;
}

.table tbody td:first-child i.pi-clock {
  color: #f39c12;
}

.table tbody td:last-child {
  width: 50px;
  text-align: center;
}

/* Input groups - Modern Flexbox */
.input-group {
  display: flex;
  align-items: center;
  gap: 6px;
}

.input-group .form-control {
  flex: 1;
  min-width: 0;
  padding: 4px 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.8rem;
  transition: border-color 0.2s ease;
}

.input-group .form-control:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.1);
}

.input-group-btn {
  display: flex;
}

/* Table container - White background with border radius */
.table-container {
  background-color: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 16px;
  margin-top: 12px;
}

/* Occurrences container - White background with border radius */
.occurrences-container {
  background-color: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 16px;
}

.occurrences-container h4 {
  margin-top: 0;
  margin-bottom: 12px;
  font-size: 0.95rem;
  font-weight: 600;
  color: #333;
}

/* Box title */
.box-title {
  font-size: 0.9rem;
  font-weight: 600;
  margin: 0 0 8px 0;
  color: #333;
}

/* Text utilities */
.text-capitalize {
  text-transform: capitalize;
}

.text-right {
  text-align: right;
}

/* Animation */
.animationIf {
  transition: all 0.3s ease;
}

/* Action buttons container */
.action-buttons-container {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  padding: 10px 0;
  justify-content: center;
}

.action-button {
  min-width: 120px;
}

.download-receipt-button {
  width: 100%;
  margin: 20px 0;
}

/* Pull right - keep for backward compatibility */
.pull-right {
  float: right;
}

/* Edit button container - separate row above tables */
.edit-button-container {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 12px;
}

/* Total section */
#infosTotal {
  margin-top: 20px;
  padding: 16px;
  background-color: #ffffff;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
}

#infosTotal h3 {
  margin: 0 0 8px 0;
  font-size: 1.3rem;
  font-weight: 700;
  color: #333;
}

#infosTotal p {
  margin: 2px 0;
  color: #666;
  font-size: 0.9rem;
}

/* Responsive */
@media (max-width: 768px) {
  .action-buttons-container {
    flex-direction: column;
  }

  .action-button {
    width: 100%;
  }


  .pull-right {
    float: none;
    margin-bottom: 10px;
  }

  .edit-button-container {
    justify-content: stretch;
  }

  .edit-button-container button {
    width: 100%;
  }

  .table {
    font-size: 0.8rem;
  }

  .table tbody td {
    padding: 5px 4px;
  }
}

@media (max-width: 480px) {
  .input-group {
    flex-direction: column;
    align-items: stretch;
  }

  .action-buttons-container {
    gap: 8px;
  }


  .table tbody td {
    padding: 4px 3px;
    font-size: 0.75rem;
  }

  .input-group .form-control {
    padding: 3px 6px;
    font-size: 0.75rem;
  }
}
</style>
